import type { Browse, Dictionary, JQueryInput } from "./types";

const classNames =
{
    browse: "browse",
    modalLayerContainer: "modalLayerContainer"
} as const;

const browseList: Dictionary<string, Browse> = {};

// The last element in the array should always contain the browse name that should receive keypresses.
const openBrowses: string[] = [];

/**
 * Adds a browse to the global browse list. If a browse with the same ID already exists, it will be overwritten.
 * @param browseIdOverride By default, the browse ID from the browse parameter will be used.
 */
export function addBrowse(browse: Browse, browseIdOverride?: string)
{
    const browseId = browseIdOverride ?? browse.id;
    browseList[browseId] = browse;
}

export function removeBrowse(browseId: string)
{
    delete browseList[browseId];
}

export function getBrowseById(browseId: string | null | undefined)
{
    return (browseId === null || browseId === undefined) ? undefined : browseList[browseId];
}

export function getAllBrowseIds()
{
    return Object.keys(browseList);
}

export function getAllBrowses()
{
    return Object.values(browseList);
}

export function browseListContains(browseId: string)
{
    return browseId in browseList;
}

/**
 * Get a Browse object for an element within the browse
 * @param elementParam An element within a browse
 * @returns The Browse containing the element, or undefined
 */
export function getContainingBrowse(elementParam: JQueryInput)
{
    const element = $(elementParam);
    const browseId = element.closest(`.${classNames.browse}`).attr("id");
    return getBrowseById(browseId);
}

/**
* Get a Browse object for the first browse within a container element
* @param elementParam An element that contains a browse
*/
export function getInnerBrowse(elementParam: JQueryInput)
{
    const element = $(elementParam);
    const browseId = element.find(`.${classNames.browse}`).first().attr("id");
    return getBrowseById(browseId);
}

/**
 * Adds a browse to the end of the list of open browses if it is not already present.
 */
export function addOpenBrowse(browseId: string)
{
    if (!openBrowses.includes(browseId))
    {
        openBrowses.push(browseId);
    }
}

/**
 * Adds a browse to the beginning of the list of open browses if it is not already present.
 */
export function prependOpenBrowse(browseId: string)
{
    if (!openBrowses.includes(browseId))
    {
        openBrowses.unshift(browseId);
    }
}

/**
 * Filters the list of open browses by a predicate function.
 */
export function filterOpenBrowses(predicate: (value: string, index: number, array: string[]) => boolean)
{
    openBrowses.splice(0, openBrowses.length, ...openBrowses.filter(predicate));
}

/**
 * @returns The index of the browse if found, otherwise -1.
 */
export function indexOfOpenBrowse(browseId: string)
{
    return openBrowses.indexOf(browseId);
}

/**
 * @returns `True` if the browse was removed, otherwise `false`.
 */
export function removeOpenBrowse(browseId: string)
{
    const index = indexOfOpenBrowse(browseId);
    if (index !== -1)
    {
        openBrowses.splice(index, 1);
        return true;
    }

    return false;
}

/**
 * Moves a browse to the end of the list of open browses if it is already present.
 * @returns `True` if the browse was moved, otherwise `false`.
 */
export function setCurrentOpenBrowse(browseId: string)
{
    const openBrowseRemoved = removeOpenBrowse(browseId);
    if (openBrowseRemoved)
    {
        addOpenBrowse(browseId);
        return true;
    }

    return false;
}

/**
 * Exposes the browse list. This function should only be used by skyward.ui.browse.ts.
 * @deprecated This function should only be used by skyward.ui.browse.ts.
 */
export function getBrowseList()
{
    return browseList;
}

/**
 * Exposes the list of open browses. This function should only be used by skyward.ui.browse.ts.
 * @deprecated This function should only be used by skyward.ui.browse.ts.
 */
export function getOpenBrowses()
{
    return openBrowses;
}

/**
 * Get the current browse name
 * @returns The browse name
 */
export function getCurrentBrowseName()
{
    // Remove browses that no longer exist in the dom
    filterOpenBrowses((browseName) => (!!document.getElementById(browseName) && browseName !== "ProfileControlsBrowse"));

    const focusedBrowse = getContainingBrowse(document.activeElement as HTMLElement);
    if (focusedBrowse && !isHiddenByModalLayer(focusedBrowse.browse) && !isHidden(focusedBrowse.browse))
    {
        return focusedBrowse.id;
    }

    // Find current browse name
    for (let i = openBrowses.length - 1; i >= 0; i--)
    {
        const browseName = openBrowses[i];
        const thisBrowse = $(document.getElementById(browseName)!);

        if (isHiddenByModalLayer(thisBrowse))
        {
            continue; // this browse is on the DOM but underneath another layer.
        }
        return browseName;
    }

    return null;

    function isHiddenByModalLayer(browse?: JQuery | null)
    {
        if (!browse)
        {
            return false;
        }
        return browse.closest(`#master, .${classNames.modalLayerContainer}`).nextAll(`.${classNames.modalLayerContainer}`).length !== 0;
    }

    function isHidden(browse?: JQuery | null)
    {
        if (!browse)
        {
            return true;
        }
        return !browse.is(":visible");
    }
}
